import { SelectField, TextField, useFormContext, useFormField } from '@calendly/form';
import { FieldError, FieldLabel, useErrorId } from '@calendly/form/parts';
import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { Grid, GridItem } from '@calendly/ui/components/grid';
import { SimpleSelect } from '@calendly/ui/components/select';
import { TextInput } from '@calendly/ui/components/text-input';
import classNames from 'classnames';
import { useEffect, useMemo, forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { isAvailableFeatureSelector } from 'features/billing';
import { useIsValueChanged } from 'hooks/use_is_value_changed';
import { hasLockingRule } from 'services/event_type_locking';
import { isNoTouchDevice } from 'services/touch_device_detection';
import { Content } from '../elements';
import { AddDurationOption } from './add_duration_option';
import { AlertBanner } from './alert_banner';
import { DurationOptionField } from './duration_option_field';
import { formatValuesToServerData } from './format_values_to_server_data';
import { getDurationInitialValues } from './get_initial_values';
import { getSelectOptions } from './get_select_options';
const spreadErrorWrapperCss = "sc6ch9y";
const SpreadErrorWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(spreadErrorWrapperCss, props.className)} />;
});
const sortedDurationOptions = durationOptions => durationOptions.slice().sort((a, b) => a - b);
export const DurationFields = ({
  className,
  hasAssignedMemberships,
  isAdminManaged,
  isRequired,
  gridSizes,
  spreadErrorMessage,
  proTip,
  hideLabel,
  eventType = {},
  useMultipleDurations = false
}) => {
  const {
    values: {
      eventDuration,
      customEventDuration,
      customEventDurationType,
      eventTypeKind,
      hasEvents,
      lockingRules
    },
    setFieldValue
  } = useFormContext();
  const {
    isGroup
  } = eventType;
  const isLocked = lockingRules && hasLockingRule(lockingRules, 'v3_duration');
  const showManagedEventWarning = isAdminManaged && isGroup && isLocked;
  const disableMangedEventDuration = showManagedEventWarning && hasAssignedMemberships;
  const [customDurationFieldProps, customDurationMeta] = useFormField({
    name: 'customEventDuration'
  });
  const disabled = eventTypeKind === 'group' && hasEvents || disableMangedEventDuration;
  const isMultipleDurationsAvailable = useSelector(state => isAvailableFeatureSelector(state, {
    featureKey: 'multiple_durations'
  }));
  const showAddDurationOption = useMultipleDurations && !isGroup;
  const [durationFieldProps, meta, durationHelpers] = useFormField({
    disabled,
    name: 'eventDuration'
  });
  const durationError = meta.error;
  const durationErrorId = useErrorId({
    durationError
  });
  const customDurationErrorId = useErrorId({
    customDurationError: customDurationMeta.error
  });
  const {
    duration: durationInMinutes
  } = formatValuesToServerData({
    eventDuration,
    customEventDuration,
    customEventDurationType
  });
  const [{
    value: durationOptions
  }, _, {
    setValue: setDurationOptions
  }] = useFormField({
    name: 'durationOptions'
  });
  const durationValue = useMemo(() => [eventDuration, customEventDuration, customEventDurationType].join(','), [eventDuration, customEventDuration, customEventDurationType]);
  const durationValueChanged = useIsValueChanged(durationValue);
  useEffect(() => {
    if (useMultipleDurations && !isMultipleDurationsAvailable && durationValueChanged) {
      setDurationOptions([]);
    }
  });
  useEffect(() => {
    if (durationOptions?.length === 1) {
      setDurationOptions([]);
    }
  }, [durationOptions, setDurationOptions]);
  const addDurationOptions = duration => {
    setDurationOptions(sortedDurationOptions([durationInMinutes, duration]));
  };
  const updateDurationOptions = duration => {
    setDurationOptions(sortedDurationOptions([...durationOptions, duration]));
  };
  const removeDurationOption = duration => {
    const isDefaultDuration = durationInMinutes === duration;
    const sortedDurationOpts = sortedDurationOptions(durationOptions.filter(d => d !== duration));
    if (isDefaultDuration || sortedDurationOpts.length === 1) {
      const nextHighestDuration = sortedDurationOpts.find(d => d > duration);
      const nextLowestDuration = sortedDurationOpts.slice().pop();
      const newDefaultDuration = nextHighestDuration || nextLowestDuration;
      const {
        eventDuration: newEventDuration,
        customEventDuration: newCustomEventDuration,
        customEventDurationType: newCustomEventDurationType
      } = getDurationInitialValues({
        duration: newDefaultDuration
      });
      setFieldValue('eventDuration', newEventDuration);
      setFieldValue('customEventDuration', newCustomEventDuration);
      setFieldValue('customEventDurationType', newCustomEventDurationType);
    }
    setDurationOptions(sortedDurationOpts);
  };

  // SimpleSelect hands back the raw option value, and formik's change handler treats
  // any non-string as an event — so the numeric presets have to bypass it.
  const selectDuration = value => {
    durationHelpers.setTouched(true);
    durationHelpers.setError(undefined);
    durationHelpers.setValue(value);
  };
  const unitSelectOptions = [{
    id: 'minutes',
    name: I18n.t('time.min_abbr')
  }, {
    id: 'hours',
    name: I18n.t('time.hours_abbr.one')
  }];
  const showDurationOptions = useMultipleDurations && isMultipleDurationsAvailable && durationOptions?.length > 1;
  if (showDurationOptions) {
    return <Content data-testid="duration-form">
        <div>
          {sortedDurationOptions(durationOptions).map(duration => <DurationOptionField key={duration} duration={duration} isDefaultDuration={durationInMinutes === duration} onClickMakeDefault={value => setFieldValue('eventDuration', value)} onClickRemove={removeDurationOption} />)}
        </div>
        <AddDurationOption defaultDuration={durationInMinutes} updateDurationOptions={updateDurationOptions} />
      </Content>;
  }
  return <Content data-testid="duration-form">
      <Grid className={className}>
        <GridItem size={gridSizes?.field || 10} sizeMobile={12}>
          <Grid>
            <GridItem size={12}>
              {!hideLabel && <FieldLabel text={I18n.t('publisher.event_types.availability.duration_fieldset.title')} id="duration-label" isRequired={isRequired} proTip={proTip} />}
              {showManagedEventWarning && (disableMangedEventDuration ? <AlertBanner>{I18n.t('publisher.event_types.availability.duration_fieldset.locked_warning')}</AlertBanner> : <AlertBanner>
                    {HtmlParser(I18n.t('publisher.event_types.availability.duration_fieldset.unlocked_warning'))}
                  </AlertBanner>)}
              <SimpleSelect aria-labelledby="duration-label" {...durationFieldProps} onChange={selectDuration} invalid={Boolean(durationError)} aria-invalid={Boolean(durationError)} aria-describedby={durationErrorId} options={getSelectOptions()} />
              <FieldError text={durationError} id={durationErrorId} />
            </GridItem>
            {eventDuration === 'custom' && <>
                <GridItem size={gridSizes?.customValue || 7}>
                  {!spreadErrorMessage ? <TextField {...customDurationFieldProps} aria-label={I18n.t('publisher.event_types.one_off.creation_modal.time_step.duration.custom_value_label')} disabled={disabled} aria-invalid={customDurationMeta.touched && !!customDurationMeta.error} inputMode="numeric" autoFocus={isNoTouchDevice()} /> : <TextInput {...customDurationFieldProps} aria-label={I18n.t('publisher.event_types.one_off.creation_modal.time_step.duration.custom_value_label')} disabled={disabled} aria-invalid={customDurationMeta.touched && !!customDurationMeta.error} inputMode="numeric" autoFocus={isNoTouchDevice()} />}
                </GridItem>
                <GridItem size={gridSizes?.customUnit || 5}>
                  <SelectField aria-label={I18n.t('publisher.event_types.one_off.creation_modal.time_step.duration.custom_unit_label')} name="customEventDurationType" disabled={disabled} options={unitSelectOptions} />
                </GridItem>
                {spreadErrorMessage && <SpreadErrorWrapper>
                    <FieldError text={customDurationMeta.error} id={customDurationErrorId} />
                  </SpreadErrorWrapper>}
              </>}
          </Grid>
        </GridItem>
      </Grid>
      {showAddDurationOption && <AddDurationOption defaultDuration={durationInMinutes} updateDurationOptions={addDurationOptions} />}
    </Content>;
};

require("./duration_fields.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./duration_fields.js");